import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../common/BackButton';
import { tireBrandsAPI } from '../../services/garageApi';
import {
  Save,
  Tag,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

const CreateTireBrand = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    image: null
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Stable preview URL for the selected image File; revoked on change/unmount
  const previewFile = formData.image instanceof File ? formData.image : null;
  const previewUrl = useMemo(
    () => (previewFile ? URL.createObjectURL(previewFile) : null),
    [previewFile]
  );
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Brand name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Brand name must be at least 2 characters';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Brand name must not exceed 100 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSaving(true);
    try {
      // Create FormData for multipart/form-data upload (including image)
      const submitData = new FormData();
      submitData.append('name', formData.name.trim());
      
      // Image file - only append if it's a File object
      if (formData.image && formData.image instanceof File) {
        submitData.append('image', formData.image);
      }

      console.log('🔍 Submitting tire brand with FormData...');
      console.log('🔍 Image included:', formData.image instanceof File);

      await tireBrandsAPI.create(submitData);

      navigate('/tire-brands', {
        state: { message: 'Tire brand created successfully!' }
      });
    } catch (error) {
      console.error('Error creating tire brand:', error);

      if (error.response?.status === 422 && error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        const newErrors = {};
        Object.keys(validationErrors).forEach(field => {
          newErrors[field] = Array.isArray(validationErrors[field]) 
            ? validationErrors[field][0] 
            : validationErrors[field];
        });
        setErrors(newErrors);
      } else if (error.response?.data?.message) {
        setErrors({ submit: error.response.data.message });
      } else {
        setErrors({ submit: 'Failed to create tire brand. Please try again.' });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <BackButton to="/tire-brands" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Add New Tire Brand</h1>
            <p className="text-sm text-gray-600 mt-1">Create a new tire manufacturer brand</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
        {errors.submit && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            <span>{errors.submit}</span>
          </div>
        )}

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Brand Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Michelin, Bridgestone, Goodyear"
                className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.name ? 'border-red-300' : 'border-gray-200'
                }`}
              />
            </div>
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
            <p className="mt-2 text-sm text-gray-500">
              Enter the name of the tire manufacturer brand
            </p>
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Brand Image
            </label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/jpg,image/webp"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  // Validate file size (5MB max)
                  if (file.size > 5120 * 1024) {
                    alert('Image size must be less than 5MB');
                    e.target.value = '';
                    return;
                  }
                  // Validate file type
                  const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
                  if (!validTypes.includes(file.type)) {
                    alert('Invalid file type. Please upload a JPEG, PNG, or WEBP image.');
                    e.target.value = '';
                    return;
                  }
                  setFormData(prev => ({ ...prev, image: file }));
                  // Clear error for this field
                  if (errors.image) {
                    setErrors(prev => ({ ...prev, image: '' }));
                  }
                }
              }}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Accepted formats: JPG, PNG, WEBP. Max size: 5MB
            </p>
            {errors.image && (
              <p className="mt-1 text-sm text-red-600">{errors.image}</p>
            )}
            {formData.image && formData.image instanceof File && (
              <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-4">
                  <img
                    src={previewUrl}
                    alt="Brand preview"
                    className="w-24 h-24 object-cover rounded-lg border border-gray-300"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      <span className="font-medium">Image selected: {formData.image.name}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {(formData.image.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-8 pt-6 border-t">
          <button
            type="button"
            onClick={() => navigate('/tire-brands')}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Create Brand
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateTireBrand;
